import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { weatherInfo, sampleRoutePoints, fetchRouteWeather, pickArrival } from '../../utils/weather';
import './RouteWeather.css';

const ProModal = lazy(() => import('../ProModal/ProModal').then(m => ({ default: m.ProModal })));

const N = 7;

const fmtTime = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

// "HH:MM" today (or tomorrow if already past) → epoch ms.
function timeToMs(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  if (d.getTime() < Date.now() - 60000) d.setDate(d.getDate() + 1);
  return d.getTime();
}

function cellFlags(wx) {
  if (!wx) return {};
  return {
    wet:   wx.code >= 51 || (wx.precip != null && wx.precip >= 50),
    cold:  wx.temp != null && wx.temp <= 4,
    windy: wx.gust != null && wx.gust >= 45,
  };
}

export function RouteWeather() {
  const { state } = useRoute();
  const { isSubscribed } = useAuth();
  const { geometry, duration, waypoints } = state.current;

  const hasRoute = waypoints.length >= 2 && (geometry?.coordinates?.length ?? 0) >= 2;

  const [departMs, setDepartMs] = useState(() => Date.now());
  const [usingNow, setUsingNow] = useState(true);
  const [hourly, setHourly]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [failed, setFailed]     = useState(false);
  const [showPro, setShowPro]   = useState(false);

  const samples = useMemo(
    () => (hasRoute ? sampleRoutePoints(geometry.coordinates, duration, N) : []),
    [geometry, duration, hasRoute]
  );
  const sig = samples.map(s => `${s.lat.toFixed(3)},${s.lng.toFixed(3)}`).join('|');

  const maxEtaH = samples.length ? Math.max(...samples.map(s => s.etaMin)) / 60 : 0;
  const offsetH = Math.max(0, (departMs - Date.now()) / 3600000);
  const daysNeeded = Math.min(7, Math.max(1, Math.ceil(offsetH + maxEtaH) / 24 + 1 | 0));

  // Fetch only when the route (or required forecast window) changes — picking
  // the arrival hour for a new departure time is done locally below.
  useEffect(() => {
    if (!isSubscribed || !sig) { setHourly(null); return; }
    const ctl = new AbortController();
    setLoading(true);
    setFailed(false);
    fetchRouteWeather(samples, daysNeeded, ctl.signal)
      .then(data => {
        if (ctl.signal.aborted) return;
        if (data) setHourly(data); else setFailed(true);
      })
      .finally(() => { if (!ctl.signal.aborted) setLoading(false); });
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, daysNeeded, isSubscribed]);

  if (!hasRoute) return null;

  // ── Free: upsell teaser ──
  if (!isSubscribed) {
    return (
      <div className="rw rw--teaser">
        <div className="rw__head">
          <span className="rw__title">Weather en route</span>
          <span className="rw__lock" aria-hidden="true">🔒</span>
        </div>
        <p className="rw__teaser-text">
          See temperature, rain and wind at every point of your ride — at the
          time you’ll actually be there.
        </p>
        <button className="rw__upsell" onClick={() => setShowPro(true)}>Unlock with Pro</button>
        {showPro && (
          <Suspense fallback={null}>
            <ProModal onClose={() => setShowPro(false)} />
          </Suspense>
        )}
      </div>
    );
  }

  // ── Pro: forecast ──
  const cells = samples.map((s, i) => {
    const arrivalMs = departMs + s.etaMin * 60000;
    const wx = hourly?.[i]?.hourly ? pickArrival(hourly[i].hourly, arrivalMs) : null;
    return { ...s, arrivalMs, wx, flags: cellFlags(wx) };
  });

  // Summary headline — worst-first.
  let summary = null;
  const wet = cells.find(c => c.flags.wet && !c.wx?.beyond);
  if (wet) {
    const label = wet.fraction === 0 ? 'the start' : wet.fraction === 1 ? 'the finish' : `${Math.round(wet.km)} km`;
    summary = `🌧 Rain likely near ${label} (${fmtTime(wet.arrivalMs)})`;
  } else if (cells.some(c => c.flags.cold)) {
    const min = Math.min(...cells.filter(c => c.wx?.temp != null).map(c => c.wx.temp));
    summary = `🥶 Cold — down to ${min}° en route`;
  } else if (cells.some(c => c.flags.windy)) {
    const g = Math.max(...cells.filter(c => c.wx?.gust != null).map(c => c.wx.gust));
    summary = `💨 Windy — gusts to ${g} km/h`;
  } else if (hourly) {
    summary = '☀ Clear conditions the whole way';
  }

  return (
    <div className="rw">
      <div className="rw__head">
        <span className="rw__title">Weather en route</span>
        <div className="rw__depart">
          <button
            className={`rw__now${usingNow ? ' rw__now--active' : ''}`}
            onClick={() => { setUsingNow(true); setDepartMs(Date.now()); }}
          >
            Now
          </button>
          <input
            className="rw__time"
            type="time"
            onChange={e => { if (e.target.value) { setUsingNow(false); setDepartMs(timeToMs(e.target.value)); } }}
            title="Departure time"
          />
        </div>
      </div>

      {summary && <div className="rw__summary">{summary}</div>}

      {loading && !hourly && (
        <div className="rw__strip">
          {samples.map((_, i) => <div key={i} className="rw-cell rw-cell--sk" />)}
        </div>
      )}

      {failed && !hourly && (
        <div className="rw__error">Weather unavailable right now — try again shortly.</div>
      )}

      {hourly && (
        <div className={`rw__strip${loading ? ' rw__strip--dim' : ''}`}>
          {cells.map((c, i) => {
            const info = c.wx ? weatherInfo(c.wx.code) : null;
            const label = c.fraction === 0 ? 'Start' : c.fraction === 1 ? 'Finish' : `${Math.round(c.km)}km`;
            const cls = c.flags.wet ? ' rw-cell--wet' : c.flags.cold ? ' rw-cell--cold' : c.flags.windy ? ' rw-cell--windy' : '';
            return (
              <div key={i} className={`rw-cell${cls}`} title={info?.label || ''}>
                <span className="rw-cell__time">{fmtTime(c.arrivalMs)}</span>
                <span className="rw-cell__km">{label}</span>
                <span className="rw-cell__icon">{c.wx?.beyond ? '–' : (info?.icon || '·')}</span>
                <span className="rw-cell__temp">{c.wx?.temp != null && !c.wx.beyond ? `${c.wx.temp}°` : '—'}</span>
                <span className="rw-cell__sub">
                  {c.wx && !c.wx.beyond
                    ? (c.flags.windy ? `${c.wx.gust} km/h` : c.wx.precip != null ? `${c.wx.precip}%` : '')
                    : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
